import {
  getChequeGiroClearingKey,
  ClearingStatusEnum,
  useCreateChequeGiroClearing,
  useUpdateChequeGiroClearing,
} from 'api-hooks/transactions/cheque-giro-clearing';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import { Form, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import {
  FullContainer,
  SeparatorHorizontal,
  SeparatorVertical,
} from 'components/styles';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { ClearingType } from './form-type';

interface Props {
  onClose: () => void;
  data?: ClearingType;
  chequeNotesId?: string | undefined;
}

const itemSchema = Yup.object().shape({
  status: Yup.string().nullable().required(),
  clearingAt: Yup.date().nullable().required(),
});

function InputForm() {
  const { t } = useTranslation();

  return (
    <>
      <FullContainer>
        <Input
          name="status"
          type="select"
          label={t('common:status')}
          placeholder={t('common:status')}
          options={[
            {
              id: ClearingStatusEnum.cleared,
              label: t(`common:${ClearingStatusEnum.cleared}`),
            },
            {
              id: ClearingStatusEnum.rejected,
              label: t(`common:${ClearingStatusEnum.rejected}`),
            },
          ]}
        />
      </FullContainer>
      <FullContainer>
        <Input
          name="clearingAt"
          type="date-time"
          label={t('common:cleared_at')}
          placeholder={t('common:cleared_at')}
        />
      </FullContainer>
    </>
  );
}

function EditForm(props: Props) {
  const { t } = useTranslation();

  const { mutateAsync } = useUpdateChequeGiroClearing();

  const defaultValues = React.useMemo<ClearingType>(() => {
    return {
      status: props.data?.status || null,
      clearingAt: props.data?.clearingAt || new Date(),
    };
  }, [props.data]);

  const resolver = useYupValidationResolver(itemSchema.required());

  const methods = useForm<ClearingType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: ClearingType) => {
      try {
        const result = await mutateAsync({
          chequeGiroClearingId: props.data!.id!,
          body: values,
        });
        notification.success({ message: result?.message });

        queryClient.refetchQueries([getChequeGiroClearingKey()[0]]);
        props.onClose();
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods.setError, mutateAsync, props],
  );

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <InputForm />
      <SeparatorVertical $margin={8} />
      <FullContainer style={{ display: 'flex', justifyContent: 'end' }}>
        <Button buttonType="tertiary" onClick={props.onClose}>
          {t('common:cancel')}
        </Button>
        <SeparatorHorizontal $margin={8} />
        <Input type="submit" />
      </FullContainer>
    </Form>
  );
}

function AddForm(props: Props) {
  const { t } = useTranslation();
  const { mutateAsync } = useCreateChequeGiroClearing();

  const defaultValues = React.useMemo<ClearingType>(() => {
    return {
      clearingAt: new Date(),
      status: null,
    };
  }, []);

  const resolver = useYupValidationResolver(itemSchema.required());

  const methods = useForm<ClearingType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: ClearingType) => {
      try {
        const result = await mutateAsync({
          chequeGiroId: props.chequeNotesId,
          body: values,
        });
        notification.success({ message: result?.message });

        queryClient.refetchQueries([getChequeGiroClearingKey()[0]]);
        props.onClose();
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods.setError, mutateAsync, props],
  );

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <InputForm />
      <SeparatorVertical $margin={8} />
      <FullContainer style={{ display: 'flex', justifyContent: 'end' }}>
        <Button buttonType="tertiary" onClick={props.onClose}>
          {t('common:cancel')}
        </Button>
        <SeparatorHorizontal $margin={8} />
        <Input type="submit" />
      </FullContainer>
    </Form>
  );
}

export default function ClearingFormPanel(props: Props) {
  const { t } = useTranslation();

  return (
    <>
      <DrawerHeader
        onClose={props.onClose}
        title={t(`common:${props.data ? 'edit' : 'add'}_extra`, {
          extra: t('common:clearing'),
        })}
      />
      <DrawerBody>
        {props.data ? <EditForm {...props} /> : <AddForm {...props} />}
      </DrawerBody>
    </>
  );
}
